import { useMemo } from "react";
import { Printer, FileDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
  PREMED_DRUGS,
  INDUCTION_DRUGS,
  MAINTENANCE_AGENTS,
  calcVolumeForDose,
} from "@/data/drugs";

/* ---------------- helpers ---------------- */
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

const fmtClock = (sec) => {
  const m = Math.floor(sec / 60).toString().padStart(2, "0");
  const s = Math.floor(sec % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
};

const drugById = (id) =>
  [...PREMED_DRUGS, ...INDUCTION_DRUGS].find((d) => d.id === id);

const routeShort = (route = "") => route.split("/")[0].trim();

const SEX_LABEL = {
  male_entire: "M",
  male_neutered: "MN",
  female_entire: "F",
  female_spayed: "FS",
};

// Build the medication rows (Section 2) from the saved plan — uses the exact
// dose the user picked on the plan screen (plan.doses), falling back to mid-range.
function buildMeds(patient, plan) {
  if (!patient || !plan) return [];
  const w = patient.weightKg;
  const sp = patient.species;
  const doses = plan.doses || {};
  const rows = [];
  const addRow = (d, phase, time) => {
    const range = sp === "cat" ? d.doseCat : d.doseDog;
    if (!range) return;
    const perKg = doses[d.id] != null ? doses[d.id] : (range.low + range.high) / 2;
    const u = range.unit.split("/")[0];
    const total = perKg * w;
    const vol = calcVolumeForDose(d, w, perKg) ?? 0;
    rows.push({
      phase,
      name: d.name,
      dose: `${total.toFixed(2)} ${u}`,
      vol: `${vol.toFixed(2)} mL`,
      route: routeShort(d.route),
      time,
    });
  };
  (plan.premeds || []).forEach((id) => {
    const d = drugById(id);
    if (d) addRow(d, "Pre-med", "T−15");
  });
  const ind = drugById(plan.induction);
  if (ind) addRow(ind, "Induction", "00:00");
  return rows;
}

/* ---------------- chart geometry ---------------- */
const AXIS_W = 48;
const COL_W = 62;
const PLOT_TOP = 26;
const PLOT_H = 250; // 220 → 10
const V_MAX = 220;
const V_MIN = 10;
const TEMP_TOP = PLOT_TOP + PLOT_H + 26;
const TEMP_H = 64; // 42 → 32
const T_MAX = 42;
const T_MIN = 32;
const EVENT_LANE = TEMP_TOP + TEMP_H + 16;

const MARKER_DEFS = [
  { key: "anaesStart", label: "Anaes start", color: "#0047AB" },
  { key: "surgeryStart", label: "Surg start", color: "#0F766E" },
  { key: "surgeryEnd", label: "Surg end", color: "#0F766E" },
  { key: "extubation", label: "Extubation", color: "#7C3AED" },
];

const sapOf = (e) => e.sap ?? Math.round(e.map + 27);
const dapOf = (e) => e.dap ?? Math.round(e.map - 13);

/* ---------------- component ---------------- */
export default function AVARecord({
  patient,
  plan,
  entries = [],
  events = [],
  markers = {},
  recovery,
  elapsedSec = 0,
}) {
  const meds = buildMeds(patient, plan);
  const maintenance =
    MAINTENANCE_AGENTS.find((a) => a.id === plan?.maintenance) ||
    MAINTENANCE_AGENTS[0];

  const drugEvents = useMemo(() => events.filter((ev) => ev.kind === "drug"), [events]);
  const nonDrugEvents = useMemo(() => events.filter((ev) => ev.kind !== "drug"), [events]);

  const allMarkers = { anaesStart: 0, ...markers };
  const maxSec = Math.max(elapsedSec, ...entries.map((e) => e.t), ...Object.values(allMarkers), 0);
  const cols = Math.max(4, Math.ceil(maxSec / 300) + 2);
  const chartW = AXIS_W + cols * COL_W + 14;

  const handlePrint = () => window.print();
  const handleExportPDF = () => exportPDF({ patient, plan, entries, events, allMarkers, recovery, elapsedSec, meds, maintenance });

  return (
    <div className="rounded-md border border-slate-300 shadow-sm overflow-hidden bg-[#FBFAF6]" data-testid="ava-record">
      {/* Title bar */}
      <div className="bg-[#0b1f33] text-white px-5 py-3 flex items-center justify-between">
        <div>
          <div className="font-bold text-sm uppercase tracking-wider flex items-center gap-2">
            Anaesthetic Record
            <span className="text-[10px] font-normal text-sky-300 tracking-widest">AVA-style monitoring chart</span>
          </div>
          <div className="text-[10px] text-slate-300 tracking-widest">
            {new Date().toLocaleDateString()} · auto-charted every 5 min
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={handlePrint} data-testid="print-log-btn" className="text-xs bg-white text-slate-800 hover:bg-slate-100 border border-slate-300">
            <Printer className="w-3 h-3 mr-1.5" /> Print
          </Button>
          <Button size="sm" onClick={handleExportPDF} data-testid="export-pdf-btn" className="text-xs bg-[#0047AB] hover:bg-[#003380] text-white">
            <FileDown className="w-3 h-3 mr-1.5" /> PDF
          </Button>
        </div>
      </div>

      {/* Section 1 — Pre-anaesthetic assessment */}
      <SectionTitle n="1" title="Pre-anaesthetic assessment" />
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 border-b border-slate-300 text-[11px]" data-testid="ava-patient">
        <Field label="Patient" value={patient?.name} />
        <Field label="Species" value={patient?.species === "cat" ? "Feline" : "Canine"} />
        <Field label="Breed" value={patient?.breed} />
        <Field label="Sex" value={SEX_LABEL[patient?.sex] || "—"} />
        <Field label="Age" value={age(patient)} />
        <Field label="Weight" value={`${patient?.weightKg?.toFixed(1) ?? "—"} kg`} />
        <Field label="BCS" value={`${patient?.bcs ?? "—"}/9`} />
        <Field label="ASA" value={patient?.asa} />
        <Field label="Procedure" value={patient?.procedure} span={2} />
        <Field label="Pre-anaes findings / bloods" value={patient?.bloods || patient?.notes || "NAD"} span={3} />
      </div>

      {/* Section 2 — Medication & fluids */}
      <SectionTitle n="2" title="Medication & fluids" />
      <MedsTable meds={meds} maintenance={maintenance} drugEvents={drugEvents} />

      {/* Section 3 & 4 — Intra-operative monitoring chart */}
      <SectionTitle n="3" title="Intra-operative monitoring" right={<ChartKey />} />
      <MonitoringChart
        entries={entries}
        allMarkers={allMarkers}
        nonDrugEvents={nonDrugEvents}
        cols={cols}
        chartW={chartW}
      />

      {/* Numeric strip (per 5-min reading) */}
      <NumericStrip entries={entries} chartW={chartW} />

      {/* Section 4 — Events & interventions log */}
      <SectionTitle n="4" title="Events, surgery times & interventions" />
      <div className="px-4 py-3 border-b border-slate-300 text-[11px]" data-testid="ava-events">
        <div className="flex flex-wrap gap-x-6 gap-y-1 mb-2 font-mono-clinical text-slate-700">
          {MARKER_DEFS.map((m) => (
            <span key={m.key}>
              <span className="text-slate-400 uppercase text-[9px] tracking-wider mr-1">{m.label}</span>
              {allMarkers[m.key] != null ? fmtClock(allMarkers[m.key]) : "— : —"}
            </span>
          ))}
        </div>
        {events.length === 0 ? (
          <div className="text-slate-400 italic">No intra-operative events recorded.</div>
        ) : (
          <ul className="space-y-0.5">
            {events.map((ev, i) => (
              <li key={`${ev.t}-${ev.kind}-${ev.label}-${i}`} className="flex items-start gap-2">
                <span className="font-mono-clinical text-slate-500 w-12 shrink-0">{fmtClock(ev.t)}</span>
                <span className={ev.kind === "complication" ? "text-[#C0392B] font-semibold" : ev.kind === "drug" ? "text-[#0047AB]" : "text-slate-700"}>
                  {ev.kind === "complication" ? "⚠ " : ev.kind === "drug" ? "💉 " : "✓ "}
                  {ev.label}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Section 5 — Recovery & post-operative */}
      <SectionTitle n="5" title="Recovery & post-operative" />
      <div className="grid grid-cols-2 md:grid-cols-4 text-[11px]" data-testid="ava-recovery">
        <Field label="Extubation time" value={allMarkers.extubation != null ? fmtClock(allMarkers.extubation) : "—"} />
        <Field label="Recovery quality" value={recovery?.quality || "—"} />
        <Field label="Post-op pain score" value={recovery?.pain ? `${recovery.pain}/4` : "—"} />
        <Field label="Recovery analgesia" value={recovery?.analgesia || "—"} />
      </div>

      <div className="px-4 py-2 bg-[#EAE6DA] text-[10px] text-slate-600 uppercase tracking-widest flex justify-between">
        <span>Total anaesthesia time: <span className="font-mono-clinical">{fmtClock(elapsedSec)}</span></span>
        <span>{maintenance.name} · O₂ carrier</span>
      </div>
    </div>
  );
}

/* ---------------- section sub-components ---------------- */
const MedsTable = ({ meds, maintenance, drugEvents }) => (
  <div className="overflow-x-auto border-b border-slate-300">
    <table className="w-full text-[11px]" data-testid="ava-meds">
      <thead className="bg-[#EAE6DA] text-slate-600 uppercase tracking-wider text-[9px]">
        <tr>
          {["Phase", "Drug (full name)", "Dose", "Volume", "Route", "Time"].map((h) => (
            <th key={h} className="px-3 py-1.5 text-left border-r border-slate-300 last:border-r-0">{h}</th>
          ))}
        </tr>
      </thead>
      <tbody className="font-clinical text-slate-800">
        {meds.map((m, i) => (
          <tr key={`${m.phase}-${m.name}-${m.time}`} className="border-t border-slate-200" data-testid={`ava-med-row-${i}`}>
            <td className="px-3 py-1.5 border-r border-slate-200 text-slate-500">{m.phase}</td>
            <td className="px-3 py-1.5 border-r border-slate-200 font-semibold">{m.name}</td>
            <td className="px-3 py-1.5 border-r border-slate-200 font-mono-clinical">{m.dose}</td>
            <td className="px-3 py-1.5 border-r border-slate-200 font-mono-clinical">{m.vol}</td>
            <td className="px-3 py-1.5 border-r border-slate-200">{m.route}</td>
            <td className="px-3 py-1.5 font-mono-clinical">{m.time}</td>
          </tr>
        ))}
        <tr className="border-t border-slate-200 bg-white/60">
          <td className="px-3 py-1.5 border-r border-slate-200 text-slate-500">Maintenance</td>
          <td className="px-3 py-1.5 border-r border-slate-200 font-semibold" colSpan={2}>{maintenance.name} in O₂</td>
          <td className="px-3 py-1.5 border-r border-slate-200" colSpan={2}>Vaporiser, see chart</td>
          <td className="px-3 py-1.5 font-mono-clinical">00:00</td>
        </tr>
        {drugEvents.map((ev, i) => (
          <tr key={`d-${ev.t}-${ev.label}`} className="border-t border-slate-200" data-testid={`ava-intraop-drug-${i}`}>
            <td className="px-3 py-1.5 border-r border-slate-200 text-[#C0392B]">Intra-op</td>
            <td className="px-3 py-1.5 border-r border-slate-200 font-semibold" colSpan={4}>{ev.label}</td>
            <td className="px-3 py-1.5 font-mono-clinical">{fmtClock(ev.t)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const MonitoringChart = ({ entries, allMarkers, nonDrugEvents, cols, chartW }) => {
  const chartH = EVENT_LANE + 22;
  const vY = (v) => PLOT_TOP + ((V_MAX - clamp(v, V_MIN, V_MAX)) / (V_MAX - V_MIN)) * PLOT_H;
  const tY = (t) => TEMP_TOP + ((T_MAX - clamp(t, T_MIN, T_MAX)) / (T_MAX - T_MIN)) * TEMP_H;
  const xAt = (sec) => AXIS_W + (sec / 300) * COL_W + COL_W / 2;
  const hrPts = entries.map((e) => `${xAt(e.t)},${vY(e.hr)}`).join(" ");
  const rrPts = entries.map((e) => `${xAt(e.t)},${vY(e.rr)}`).join(" ");
  const tempPts = entries.map((e) => `${xAt(e.t)},${tY(e.temp)}`).join(" ");

  return (
    <div className="overflow-x-auto bg-white" data-testid="ava-chart">
      <svg width={chartW} height={chartH} className="block">
        {/* vitals grid 10..220 */}
        {gridValues(V_MIN, V_MAX, 20).map((v) => (
          <g key={`vg${v}`}>
            <line x1={AXIS_W} y1={vY(v)} x2={chartW - 6} y2={vY(v)} stroke={v % 40 === 0 ? "#d4cfc0" : "#ece8dc"} strokeWidth="1" />
            <text x={AXIS_W - 6} y={vY(v) + 3} textAnchor="end" fontSize="8" className="fill-slate-400 font-mono">{v}</text>
          </g>
        ))}
        {/* temperature grid 32..42 */}
        {gridValues(T_MIN, T_MAX, 2).map((t) => (
          <g key={`tg${t}`}>
            <line x1={AXIS_W} y1={tY(t)} x2={chartW - 6} y2={tY(t)} stroke="#f1e7df" strokeWidth="1" />
            <text x={AXIS_W - 6} y={tY(t) + 3} textAnchor="end" fontSize="8" className="fill-orange-300 font-mono">{t}</text>
          </g>
        ))}
        <text x={4} y={PLOT_TOP + 8} fontSize="8" className="fill-slate-500 font-semibold">bpm/</text>
        <text x={4} y={PLOT_TOP + 18} fontSize="8" className="fill-slate-500 font-semibold">mmHg</text>
        <text x={4} y={TEMP_TOP + 8} fontSize="8" className="fill-orange-400 font-semibold">°C</text>

        {/* time columns + labels */}
        {Array.from({ length: cols }).map((_, c) => {
          const x = AXIS_W + c * COL_W;
          return (
            <g key={`col${c}`}>
              <line x1={x} y1={PLOT_TOP} x2={x} y2={TEMP_TOP + TEMP_H} stroke="#ece8dc" strokeWidth="1" />
              <text x={x + COL_W / 2} y={PLOT_TOP - 8} textAnchor="middle" fontSize="8" className="fill-slate-500 font-mono">{fmtClock(c * 300)}</text>
            </g>
          );
        })}

        {/* event / surgery markers (Section 4) */}
        {MARKER_DEFS.map((m, mi) => {
          if (allMarkers[m.key] == null) return null;
          const x = xAt(allMarkers[m.key]);
          return (
            <g key={m.key} data-testid={`ava-marker-${m.key}`}>
              <line x1={x} y1={PLOT_TOP} x2={x} y2={TEMP_TOP + TEMP_H} stroke={m.color} strokeWidth="1.4" strokeDasharray="4 3" />
              <rect x={x - 1.5} y={PLOT_TOP} width="3" height="3" fill={m.color} />
              <text x={x + 3} y={PLOT_TOP + 8 + mi * 9} fontSize="8" fill={m.color} className="font-semibold">{m.label}</text>
            </g>
          );
        })}

        {/* BP whiskers: SAP ˅ (top), DAP ˄ (bottom), MAP – */}
        {entries.map((e) => {
          const x = xAt(e.t);
          const ys = vY(sapOf(e));
          const yd = vY(dapOf(e));
          const ym = vY(e.map);
          return (
            <g key={`bp-${e.t}`} stroke="#C0392B" fill="none">
              <line x1={x} y1={ys} x2={x} y2={yd} strokeWidth="1.2" />
              <polyline points={`${x - 4},${ys - 4} ${x},${ys} ${x + 4},${ys - 4}`} strokeWidth="1.3" />
              <polyline points={`${x - 4},${yd + 4} ${x},${yd} ${x + 4},${yd + 4}`} strokeWidth="1.3" />
              <line x1={x - 4} y1={ym} x2={x + 4} y2={ym} strokeWidth="2" />
            </g>
          );
        })}

        {/* HR ● connected */}
        {entries.length > 1 && <polyline points={hrPts} fill="none" stroke="#0E7C5A" strokeWidth="1.2" opacity="0.6" />}
        {entries.map((e, i) => (
          <circle key={`hr-${e.t}`} cx={xAt(e.t)} cy={vY(e.hr)} r="3" fill="#0E7C5A" data-testid={i === entries.length - 1 ? "ava-hr-last" : undefined} />
        ))}

        {/* RR ○ connected */}
        {entries.length > 1 && <polyline points={rrPts} fill="none" stroke="#2563EB" strokeWidth="1" strokeDasharray="3 2" opacity="0.6" />}
        {entries.map((e) => (
          <circle key={`rr-${e.t}`} cx={xAt(e.t)} cy={vY(e.rr)} r="3" fill="white" stroke="#2563EB" strokeWidth="1.4" />
        ))}

        {/* Temperature line */}
        {entries.length > 1 && <polyline points={tempPts} fill="none" stroke="#EA8A0B" strokeWidth="1.4" />}
        {entries.map((e) => (
          <rect key={`tp-${e.t}`} x={xAt(e.t) - 2} y={tY(e.temp) - 2} width="4" height="4" fill="#EA8A0B" />
        ))}

        {/* complication / intervention ticks */}
        {nonDrugEvents.map((ev) => (
          <g key={`ev-${ev.t}-${ev.label}`}>
            <title>{`${fmtClock(ev.t)} · ${ev.label}`}</title>
            <circle cx={xAt(ev.t)} cy={EVENT_LANE} r="3" fill={ev.kind === "complication" ? "#DC2626" : "#2563EB"} />
          </g>
        ))}
        <text x={4} y={EVENT_LANE + 3} fontSize="8" className="fill-slate-400">events</text>

        {entries.length === 0 && (
          <text x={chartW / 2} y={PLOT_TOP + PLOT_H / 2} textAnchor="middle" fontSize="11" className="fill-slate-400">
            First reading charts at 00:00, then every 5 minutes…
          </text>
        )}
      </svg>
    </div>
  );
};

const NUMERIC_ROWS = [
  ["Time", (e) => fmtClock(e.t)],
  ["HR", (e) => Math.round(e.hr)],
  ["RR", (e) => Math.round(e.rr)],
  ["SAP/MAP/DAP", (e) => `${sapOf(e)}/${Math.round(e.map)}/${dapOf(e)}`],
  ["SpO₂ %", (e) => Math.round(e.spo2)],
  ["EtCO₂", (e) => Math.round(e.etco2)],
  ["Temp °C", (e) => e.temp.toFixed(1)],
  ["Eye", (e) => e.eye || "—"],
  ["Palpebral", (e) => e.palp || "—"],
  ["Jaw tone", (e) => e.jaw || "—"],
  ["Vap %", (e) => e.iso.toFixed(1)],
  ["O₂ L/min", (e) => e.o2.toFixed(1)],
];

const NumericStrip = ({ entries, chartW }) => (
  <div className="overflow-x-auto border-t border-slate-300 bg-[#FBFAF6]">
    <table className="text-[10px] border-collapse" style={{ minWidth: chartW }} data-testid="ava-numeric">
      <tbody>
        {NUMERIC_ROWS.map(([label, fn], r) => (
          <tr key={label} className={r === 0 ? "bg-[#EAE6DA]" : "odd:bg-white/50"}>
            <td className="px-2 py-1 border border-slate-200 font-semibold text-slate-600 sticky left-0 bg-inherit" style={{ width: AXIS_W }}>
              {label}
            </td>
            {entries.length === 0 ? (
              <td className="px-2 py-1 border border-slate-200 text-slate-400 italic">—</td>
            ) : (
              entries.map((e, c) => (
                <td key={`${label}-${e.t ?? c}`} className="px-1 py-1 border border-slate-200 text-center font-mono-clinical text-slate-700" style={{ width: COL_W }}>
                  {fn(e)}
                </td>
              ))
            )}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

/* ---------------- generic sub-components ---------------- */
const SectionTitle = ({ n, title, right }) => (
  <div className="flex items-center justify-between bg-[#0b1f33]/[0.04] border-y border-slate-300 px-4 py-1.5">
    <div className="flex items-center gap-2">
      <span className="w-4 h-4 rounded-sm bg-[#0b1f33] text-white text-[9px] font-bold flex items-center justify-center">{n}</span>
      <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-700">{title}</span>
    </div>
    {right}
  </div>
);

const Field = ({ label, value, span = 1 }) => (
  <div
    className="px-3 py-1.5 border-r border-b border-slate-200"
    style={span > 1 ? { gridColumn: `span ${span} / span ${span}` } : undefined}
  >
    <div className="text-[9px] uppercase tracking-widest text-slate-400">{label}</div>
    <div className="font-clinical font-semibold text-slate-800 truncate">{value || "—"}</div>
  </div>
);

const ChartKey = () => (
  <div className="flex items-center gap-3 text-[9px] font-mono text-slate-600">
    <span className="flex items-center gap-1"><b className="text-[#0E7C5A]">●</b> HR</span>
    <span className="flex items-center gap-1"><b className="text-[#2563EB]">○</b> RR</span>
    <span className="flex items-center gap-1 text-[#C0392B]"><b>˅</b>SAP <b>–</b>MAP <b>˄</b>DAP</span>
    <span className="flex items-center gap-1"><b className="text-[#EA8A0B]">▪</b> Temp</span>
  </div>
);

/* ---------------- utils ---------------- */
function gridValues(min, max, step) {
  const out = [];
  for (let v = min; v <= max; v += step) out.push(v);
  return out;
}

function age(patient) {
  if (!patient) return "—";
  const y = patient.ageYears;
  const m = patient.ageMonths;
  if (!y && !m) return "—";
  return `${y || 0}y ${m || 0}m`;
}

/* ---------------- PDF export (vector chart) ---------------- */
function exportPDF({ patient, plan, entries, events, allMarkers, recovery, elapsedSec, meds, maintenance }) {
  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();

  doc.setFont("helvetica", "bold");
  doc.setFontSize(15);
  doc.text("ANAESTHETIC RECORD", 36, 34);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.text("AVA-style monitoring chart", 36, 46);
  doc.text(new Date().toLocaleString(), pageW - 36, 46, { align: "right" });

  // Section 1 strip
  const info = [
    `Patient: ${patient?.name || "—"}`,
    `${patient?.species === "cat" ? "Feline" : "Canine"} · ${patient?.breed || "—"} · ${SEX_LABEL[patient?.sex] || "—"}`,
    `Wt: ${patient?.weightKg?.toFixed(1) ?? "—"} kg · BCS ${patient?.bcs ?? "—"}/9 · ASA ${patient?.asa || "—"}`,
    `Procedure: ${patient?.procedure || "—"}`,
  ];
  doc.setFontSize(8);
  doc.setTextColor(40);
  info.forEach((t, i) => doc.text(t, 36 + (i % 2) * 360, 62 + Math.floor(i / 2) * 12));
  if (patient?.bloods) doc.text(`Bloods: ${String(patient.bloods).slice(0, 90)}`, 36, 90);

  // Meds table
  autoTable(doc, {
    startY: 98,
    head: [["Phase", "Drug", "Dose", "Volume", "Route", "Time"]],
    body: [
      ...meds.map((m) => [m.phase, m.name, m.dose, m.vol, m.route, m.time]),
      ["Maintenance", `${maintenance.name} in O₂`, "vaporiser", "", "", "00:00"],
      ...events.filter((e) => e.kind === "drug").map((e) => ["Intra-op", e.label, "", "", "", fmtClock(e.t)]),
    ],
    headStyles: { fillColor: [11, 31, 51], fontSize: 7 },
    bodyStyles: { fontSize: 7 },
    margin: { left: 36, right: 36 },
    tableWidth: pageW - 72,
  });

  // Vector chart
  const top = doc.lastAutoTable.finalY + 14;
  const axisW = 30;
  const left = 36 + axisW;
  const plotH = 150;
  const plotW = pageW - 72 - axisW;
  const maxSec = Math.max(elapsedSec, ...entries.map((e) => e.t), ...Object.values(allMarkers), 1);
  const cols = Math.max(12, Math.ceil(maxSec / 300));
  const xAt = (s) => left + (s / (cols * 300)) * plotW;
  const vy = (v) => top + ((220 - clamp(v, 10, 220)) / 210) * plotH;

  doc.setDrawColor(220);
  doc.setLineWidth(0.4);
  for (let v = 20; v <= 220; v += 20) {
    doc.line(left, vy(v), left + plotW, vy(v));
    doc.setFontSize(6); doc.setTextColor(150);
    doc.text(String(v), left - 4, vy(v) + 2, { align: "right" });
  }
  for (let c = 0; c <= cols; c++) {
    const x = left + (c / cols) * plotW;
    doc.line(x, top, x, top + plotH);
    doc.setFontSize(6); doc.setTextColor(150);
    doc.text(fmtClock(c * 300), x, top + plotH + 8, { align: "center" });
  }
  // markers
  MARKER_DEFS.forEach((m) => {
    if (allMarkers[m.key] == null) return;
    const x = xAt(allMarkers[m.key]);
    doc.setDrawColor(120, 80, 160); doc.setLineDashPattern([2, 2], 0);
    doc.line(x, top, x, top + plotH); doc.setLineDashPattern([], 0);
    doc.setFontSize(5); doc.setTextColor(90);
    doc.text(m.label, x + 1, top + 6);
  });
  // BP + HR + RR
  entries.forEach((e) => {
    const x = xAt(e.t);
    const sap = e.sap ?? e.map + 27, dap = e.dap ?? e.map - 13;
    doc.setDrawColor(192, 57, 43); doc.setLineWidth(0.5);
    doc.line(x, vy(sap), x, vy(dap));
    doc.line(x - 3, vy(e.map), x + 3, vy(e.map));
    doc.setFillColor(14, 124, 90); doc.circle(x, vy(e.hr), 1.6, "F");
    doc.setDrawColor(37, 99, 235); doc.setFillColor(255, 255, 255); doc.circle(x, vy(e.rr), 1.6, "FD");
  });
  // HR line
  doc.setDrawColor(14, 124, 90); doc.setLineWidth(0.5);
  for (let i = 1; i < entries.length; i++) {
    doc.line(xAt(entries[i - 1].t), vy(entries[i - 1].hr), xAt(entries[i].t), vy(entries[i].hr));
  }

  // numeric table
  autoTable(doc, {
    startY: top + plotH + 16,
    head: [["", ...entries.map((e) => fmtClock(e.t))]],
    body: [
      ["HR", ...entries.map((e) => Math.round(e.hr))],
      ["RR", ...entries.map((e) => Math.round(e.rr))],
      ["BP S/M/D", ...entries.map((e) => `${e.sap ?? Math.round(e.map + 27)}/${Math.round(e.map)}/${e.dap ?? Math.round(e.map - 13)}`)],
      ["SpO2", ...entries.map((e) => Math.round(e.spo2))],
      ["EtCO2", ...entries.map((e) => Math.round(e.etco2))],
      ["Temp", ...entries.map((e) => e.temp.toFixed(1))],
      ["Eye", ...entries.map((e) => e.eye || "-")],
      ["Vap%", ...entries.map((e) => e.iso.toFixed(1))],
    ],
    headStyles: { fillColor: [234, 230, 218], textColor: 60, fontSize: 6 },
    bodyStyles: { fontSize: 6 },
    margin: { left: 36, right: 36 },
  });

  // recovery footer
  const ry = doc.lastAutoTable.finalY + 12;
  doc.setFontSize(8); doc.setTextColor(40); doc.setFont("helvetica", "bold");
  doc.text("Recovery:", 36, ry); doc.setFont("helvetica", "normal");
  doc.text(
    `Extubation ${allMarkers.extubation != null ? fmtClock(allMarkers.extubation) : "—"} · Quality: ${recovery?.quality || "—"} · Pain ${recovery?.pain ? recovery.pain + "/4" : "—"} · Analgesia: ${recovery?.analgesia || "—"}`,
    82, ry
  );

  doc.save(`anaesthetic_record_${patient?.name || "patient"}_${new Date().toISOString().slice(0, 10)}.pdf`);
}
